/** Search equivalence only. Never canonicalise or rewrite listing identities:
 * Mini and Moke remain separate makes. All three search terms deliberately
 * include the entire Mini/Moke fleet, not just Mini's Moke model. */
const MINI_MOKE_MAKES = ["Mini", "Moke", "Mini Moke"] as const;
/** Legacy cars retain their actual Morris/Austin marque. Match exact models,
 * not the entire Morris/Austin fleets and never rewrite their identities. */
export const MINI_MOKE_MODELS = ["Mini Moke", "Rover Mini Moke"] as const;
/** The live index has no model facet. Route these searches to the canonical
 * API instead of silently omitting Austin/Morris model matches. */
export function requiresCanonicalMakeSearch(input: unknown): boolean {
  if (typeof input === "string") {
    const params = new URLSearchParams(input);
    return Boolean(makeSearchAliases(params.get("query") ?? "")) || hasAliasFacet(params.get("facetFilters"));
  }
  return record(input) && (
    (typeof input.query === "string" && Boolean(makeSearchAliases(input.query))) ||
    hasAliasFacet(input.facetFilters)
  );
}

export function makeSearchAliases(value: string): readonly string[] | null {
  const key = value.trim().toLowerCase().replace(/\s+/g, " ");
  return ["mini", "moke", "mini moke"].includes(key) ? MINI_MOKE_MAKES : null;
}

export function expandMakeSearchValues(values: readonly string[]): string[] {
  return [...new Set(values.flatMap(value => [...(makeSearchAliases(value) ?? [value])]))];
}

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function expandFacet(value: string): string[] {
  const match = /^make:(?:"([^"]+)"|(.+))$/.exec(value);
  const aliases = match && makeSearchAliases(match[1] ?? match[2]);
  return aliases ? aliases.map(make => `make:${make}`) : [value];
}

function hasAliasFacet(value: unknown): boolean {
  if (Array.isArray(value)) return value.some(hasAliasFacet);
  if (typeof value !== "string") return false;
  if (expandFacet(value).length > 1) return true;
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) && hasAliasFacet(parsed);
  } catch { return false; }
}

function expandFacets(value: unknown): unknown {
  if (typeof value === "string") {
    // Legacy InstantSearch can serialize facetFilters as a JSON parameter.
    try {
      const parsed: unknown = JSON.parse(value);
      if (Array.isArray(parsed)) return JSON.stringify(expandFacets(parsed));
    } catch { /* A single ordinary facet filter is not JSON. */ }
    const expanded = expandFacet(value);
    return expanded.length > 1 ? [expanded] : value;
  }
  if (!Array.isArray(value)) return value;
  return value.map(clause => {
    if (typeof clause === "string") {
      const expanded = expandFacet(clause);
      return expanded.length > 1 ? expanded : clause;
    }
    return Array.isArray(clause)
      ? [...new Set(clause.flatMap(item => typeof item === "string" ? expandFacet(item) : [item]))]
      : clause;
  });
}

/** Preserve AND between attributes and OR within selected makes. Query aliases
 * become an exact make group, rather than an unreliable multiword text match.
 * Dates, country, availability, pagination, sorting and model filters survive. */
export function expandAlgoliaMakeSearchParams<T>(input: T): T {
  if (typeof input === "string") {
    const params = new URLSearchParams(input);
    const query = params.get("query");
    const aliases = query !== null ? makeSearchAliases(query) : null;
    const facets = params.get("facetFilters");
    if (!aliases && !hasAliasFacet(facets)) return input;
    const expanded = expandAlgoliaMakeSearchParams({
      ...(query !== null ? { query } : {}),
      ...(facets !== null ? { facetFilters: (() => {
        try { return JSON.parse(facets) as unknown; } catch { return facets; }
      })() } : {}),
    });
    if (aliases) params.set("query", "");
    if ("facetFilters" in expanded) params.set("facetFilters", JSON.stringify(expanded.facetFilters));
    return params.toString() as T;
  }
  if (!record(input)) return input;
  const aliases = typeof input.query === "string" ? makeSearchAliases(input.query) : null;
  if (!aliases && !hasAliasFacet(input.facetFilters)) return input;
  const facetFilters = expandFacets(input.facetFilters);
  const output: Record<string, unknown> = { ...input,
    ...(input.facetFilters !== undefined ? { facetFilters } : {}),
  };
  if (aliases) {
    let filters: unknown = facetFilters;
    if (typeof filters === "string") {
      try { filters = JSON.parse(filters); } catch { filters = [filters]; }
    }
    output.query = "";
    output.facetFilters = [...(Array.isArray(filters) ? filters : []),
      aliases.map(make => `make:${make}`)];
  }
  return output as T;
}

/** Rewrite transport copies only. Original InstantSearch request stamps and
 * browser URL/UI state must retain the user's term and selected make. */
export function expandAlgoliaMakeSearchArgs(args: readonly unknown[]): unknown[] {
  const rewrite = (request: unknown): unknown => {
    if (!record(request)) return request;
    return request.params !== undefined
      ? { ...request, params: expandAlgoliaMakeSearchParams(request.params) }
      : expandAlgoliaMakeSearchParams(request);
  };
  const first = args[0];
  const next = Array.isArray(first) ? first.map(rewrite)
    : record(first) && Array.isArray(first.requests)
      ? { ...first, requests: first.requests.map(rewrite) } : rewrite(first);
  return [next, ...args.slice(1)];
}
